import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import PropTypes, { func } from "prop-types";
import cx from "classnames";
import { cloneDeep } from "lodash";
import { app } from "../../../values/ui";
import Loading from "../../../blocks/Loading";
import Caret from "../../../../Global/svg/Caret";
import { Tooltip } from "../../../../Global/Tooltip";
import StudentManagementMenu from "Components/Reports/blocks/StudentManagement/Menu";
import { logSessionInfo } from "Services/logger";
import { activityStatus } from "Constants/constants";
import moment from "moment";
import { statusTooltips } from "Services/displayStatus";
import guideIcon from "../../../../../images/icons/guide-icon.svg";
import rosterCreators from "../../../state/roster/creators";
import icons from "./icons";
import LanguageSelect from "./LanguageSelect";
import assignmentActions from "../../../state/assignment/actions";
import Button from "../../../blocks/Button";
import { getUserData } from "../../../../../services/API";
import { updateBatchAssignments } from "../../../../../services/assignment/updateBatchAssignments";
import FaCheck from "react-icons/lib/fa/check";
import FaHandPointUp from "react-icons/lib/fa/hand-o-up";
import { ASSIGNMENT_TYPE, ENTITY_TYPE } from "../../../../../services/assignment/constants";
import ScaffoldIcon from './ScaffoldIcon'
import { filterLocales } from "../../../../../services/assignment/manifest";
import PracticeModal from './PracticeModal'
import WarningModal from './WarningModal'
import TrackingModal from "./TrackingModal"
import { GetBrandedAppName, GetBrandKey, GetTrackingReportConfig } from "../../../../../services/BrandingService";
import { useTranslation } from "react-i18next";

const headers = app.trackingTable.headers;
// ABSTRACT ME FOR GEN TABLE REUSE
const Header = (props) => {
  const { sorts, checked, onChangeCheckbox } = props;

  const {t} = useTranslation('tracking_report')

  // newline splits -> tooltips
  return (
    <thead className="tracking-table-header">
      <tr className="tracking-table-header-row">
        <th style={{ width: 40 }}>
          <input
            type="checkbox"
            checked={checked}
            onChange={onChangeCheckbox}
          />
        </th>
        {headers.map((it, i) => {
          let value = it.value;
          let found = false; //
          let iconClass = "tracking-table-header-icon";
          let down = false;
          if (sorts.length > 0) {
            found = sorts[0].type === value;
            down = sorts[0].dir === "asc" ? true : false;
          }

          const clickable = !!it.value;

          // dump me for clean class naming
          let cls = `tracking-table-header-cell ${found && "active"}`;

          const onClick = clickable ? () => props.onClick(value) : null;

          return (
            <th
              key={i}
              onClick={onClick}
              className={cls}
              style={{
                minWidth: it.width,
                cursor: clickable ? "pointer" : "auto",
              }}
            >
              <span className="tracking-table-header-data">
                <div className="tracking-table-header-title">
                  {t(it.label)}
                  <br />
                  {t(it.subtitle)}
                </div>
                {it.value && found && (
                  <Caret iconClass={iconClass} down={down} />
                )}
              </span>
            </th>
          );
        })}
      </tr>
    </thead>
  );
};

const Row = ({ data, onRowSelected, isSelected, isLoading, onDeepLink, index, openModal }) => {
  const { tutoringMinutes, storyCount, status } = data;

  const selectedClass = cx({
    "tracking-table-body-row": true,
    "selected-row": isSelected,
    error:
      !data.pendingAssignment &&
      data.assessmentStatus &&
      data.assessmentStatus.needsAttention,
    highlight:
      !data.pendingAssignment &&
      data.assessmentStatus &&
      data.assessmentStatus.started &&
      !data.assessmentStatus.needsAttention,
  });

  const minutesCellStyle = {};
  if (tutoringMinutes >= 10) {// From 10 - 29
    minutesCellStyle.background = "var(--at-grade)";
    minutesCellStyle.color = "black";
  }
  if (tutoringMinutes >= 30) minutesCellStyle.background = "var(--ahead-grade)";// 30+

  const countCellStyle = {};
  if (storyCount === 0) countCellStyle.background = "var(--below-grade)";
  if (storyCount > 0) {// From 1 - 4
    countCellStyle.background = "var(--at-grade)";
    countCellStyle.color = "black";
  }
  if (storyCount > 4) countCellStyle.background = "var(--ahead-grade)";// 5+

  const showSkillsIcon = tutoringMinutes > 0 && storyCount > 0;

  const isClickableMin = tutoringMinutes > 0;
  const isClickableCount = storyCount > 0;
  return (
    <tr className={selectedClass} onClick={onClickRow}>
      <td style={{ textAlign: "center" }}>
        <input type="checkbox" readOnly checked={isSelected} />
      </td>
      <Cell>{data.last_name}</Cell>
      <Cell>{data.first_name}</Cell>
      <Cell onClick={isClickableMin && openModal}>
        <div style={minutesCellStyle} className="tracking-practice-status-cell">
          {tutoringMinutes} min
        </div>
        {isClickableMin && <FaHandPointUp style={{marginLeft: 6}}/>}
      </Cell>
      <Cell onClick={isClickableCount && openModal}>
        <div className="flex-row">
          <div style={countCellStyle} className="tracking-practice-count-cell">
            {storyCount}
          </div>
          {data.metadata.early_reader && <ScaffoldIcon language='english' />}
          {data.metadata.esl && data.metadata.spanish_early_reader && <ScaffoldIcon language='spanish' />}
          {isClickableCount && <FaHandPointUp style={{marginLeft: 6}}/>}
        </div>
      </Cell>
      <Cell>
        {showSkillsIcon && (
          <img
            src={icons.skillsIcon}
            style={{ height: 40, border: "2px solid #52aebd" }}
            onClick={(e) => {
              e.stopPropagation();
              onDeepLink(data);
            }}
          />
        )}
      </Cell>
      <AssessmentCell
        id={data.studentId}
        status={status}
        isLoading={isLoading}
        data={data}
      />
      <Cell onClick={(e) => e.stopPropagation()}>
        <LanguageSelect student={data} index={index} />
      </Cell>
    </tr>
  );

  function onClickRow() {
    onRowSelected(data);
  }
};

const Cell = (props) => {

  return (
    <td
      onClick={onClick}
      className="tracking-table-body-cell"
      style={props.style}
    >
      {props.children}
    </td>
  );

  function onClick(e) {
    if (props.onClick) props.onClick(e);
  }
};

const AssessmentCell = ({ id, status, isLoading, data }) => {

  const {t} = useTranslation('tracking_report')

  if (isLoading) {
    return (
      <td className="tracking-table-body-cell trackingAssessment">
        <Loading />
      </td>
    );
  }

  if (status) {
    const statusId = status.toLowerCase()
    const statusTooltip =
      `<div>
        <div><b>${t(statusId)}</b> ${t('means')} ${t(`${statusId}_description`)}</div>
        <div class="amiraTooltipSection"><img src="${guideIcon}" alt=""/>${t(`${statusId}_cta`)}</div>
      </div>`

    // TODO: Uncomment this once 'unassessed_description_nwea-map-tutor_override' and 'unassessed_cta_nwea-map-tutor_override' translations are done.
    // const statusDescriptionKey = `${statusId}_description_${GetBrandKey()}_override`;
    // const statusDescriptionOverride = t(statusDescriptionKey);
    // const statusCTAKey = `${statusId}_cta_${GetBrandKey()}_override`;
    // const statusCTAOverride = t(statusCTAKey);

    // const statusTooltip =
    //   `<div>
    //     <div><b>${t(statusId)}</b> ${t('means')} ${statusDescriptionOverride === statusDescriptionKey ? t(`${statusId}_description`) : statusDescriptionOverride}</div>
    //     <div class="amiraTooltipSection"><img src="${guideIcon}" alt=""/>${statusCTAOverride === statusCTAKey ? t(`${statusId}_cta`) : statusCTAOverride}</div>
    //   </div>`

    const color = getStatusColor();
    const background = `${color}28`;
    const Icon = getIcon();

    return (
      <td className="tracking-table-body-cell trackingAssessment">
        <div className="assessment-status-box-bg">
          <div className="assessment-status-box" style={{ color, background }}>
            {!!Icon && <Icon background={background} />}
            <span className="assignmentStatusLabel">
              {getLabel()}
              {statusTooltip && (
                <span className="assignmentStatusTooltip">
                  <Tooltip type={id} content={statusTooltip} />
                </span>
              )}
            </span>
          </div>
        </div>
      </td>
    );
  }
  return <td className="tracking-table-body-cell trackingAssessment"></td>;

  function getIcon() {
    const id = status;

    if (id === "ERROR") return icons.redoIcon;
    // if (id === "ASSIGNED") return icons.calendarIcon;
    if (["ASSIGNED", "DISTRICT_ASSIGNED"].includes(id)) return icons.calendarIcon;
    if (id === "UNDER_REVIEW") return icons.clockIcon;
    if (["INCOMPLETE"].includes(id)) return icons.alertIcon;
    if (["COMPLETE", "RESCORED", "PRE-READER"].includes(id))
      return icons.starsIcon;
    if (["SCORING", "IN_PROGRESS"].includes(id)) return icons.targetIcon;
  }

  function getStatusColor() {
    const id = status;

    if (id === "PRE-READER") return "#fcaf4e";
    if (id === "REASSESS") return "#d70000";
    if (["INCOMPLETE", "ERROR"].includes(id)) return "#ff511c";
    if (["COMPLETE", "RESCORED"].includes(id)) return "#00a563";
    if (id === "SCORING") return "#009e9a";
    if (id === "UNDER_REVIEW") return "#64d177";
    if (["IN_PROGRESS", "ASSIGNED", "DISTRICT_ASSIGNED"].includes(id)) return "#2c92d0";
    if (id === "UNASSESSED") return "#aaadb8";
  }

  function getLabel() {
    const {dateCreated, dateCompleted} = data

    let date = ""
    if (dateCreated) date = moment(dateCreated).format("M/D")
    if (dateCompleted) date = moment(dateCompleted).format('M/D')

    let month, day;
    if (date) [month, day] = date.split("/")

    return t(`${status.toLowerCase()}_label`, {month, day})
  }
};

export default function Tracking(props) {
  const {
    sorts,
    onScroll,
    isScrolled,
    isPastWindow,
  } = props;

  const {t} = useTranslation('tracking_report')

  const [reportData, setReportData] = useState([]);
  const [lookup, setLookup] = useState({});
  const [selected, setSelected] = useState([]);
  const [loadingSelected, setLoadingSelected] = useState([]);
  const [practiceModalData, setPracticeModalData] = useState(null)
  const [warningModalData, setWarningModalData] = useState(null)
  const [showModal, setShowModal] = useState(false);
  const [currentStudent, setCurrentStudent] = useState(null);

  const dispatch = useDispatch();
  const rosterSelect = (selection) =>
    dispatch(rosterCreators.rosterSelect(selection));
  const setAssignmentState = (state) =>
    dispatch(assignmentActions.setState(state));

  useEffect(() => {
    if (!props.reportData) return;

    const lookup = {};
    const data = cloneDeep(props.reportData);
    for (const row of data) {
      const { status, entityType } = row;

      if (row.status === "COMPLETE"){
        row.status = row.assignmentType !== ASSIGNMENT_TYPE.CALIBRATION ? "SCORING" : "DONE";
      }

      if (row.status === "DONE") row.status = "COMPLETE";

      // You can't unassign a SCHOOL or DISTRICT assignment
      if([ENTITY_TYPE.DISTRICT, ENTITY_TYPE.SCHOOL].includes(entityType)) {
        row.isUnassignable = false;
      }

      // If this is a STUDENT assignment
      if(entityType === ENTITY_TYPE.STUDENT) {
        // You can unassign it only if the status is ASSIGNED or IN_PROGRESS
        row.isUnassignable = [
          "ASSIGNED",
          "IN_PROGRESS",
        ].includes(status);
      }

      row.isAssignable = ![
        "INCOMPLETE",
        "IN_PROGRESS",
        "DISTRICT_ASSIGNED"
      ].includes(status);
      lookup[row.studentId] = row;
    }
    setReportData(data);
    setLookup(lookup);
    setLoadingSelected([]);
    setSelected([])
  }, [props.reportData]);

  function tableSort(data) {
    props.onSort && props.onSort(data);
  }

  const selectable = reportData.map((s) => s.studentId);
  const isHeaderChecked = selectable.toString() === selected.toString();

  function selectAll() {
    if (isPastWindow) return;
    if (isHeaderChecked) setSelected([]);
    else setSelected(selectable);
  }

  function onRowSelected({ studentId }) {
    const update = Array.from(selected);
    const index = update.indexOf(studentId);
    if (index > -1) update.splice(index, 1);
    else update.push(studentId);
    setSelected(update);
  }

  const tableStyles = cx({
    "tracking-table": true,
    unscrolled: !isScrolled,
  });

  const isAssignableSelection =
    selected[0] && selected.some((s) => lookup[s].isAssignable);

  const isUnassignableSelection =
    selected[0] && selected.every((s) => lookup[s].isUnassignable);

  const shouldShowButton = (buttonType) => {
    const defaultConfig = {
      benchmark: true,
      progressMonitoring: true,
      calibration: true,
      practice: true,
      districtAssessment: true,
      unassign: true,
    };
    const config = GetTrackingReportConfig() || defaultConfig;
    return config[buttonType];
  };

  return (
    <div className="trackingTableBody">
      {showModal &&
      <TrackingModal
        student={currentStudent}
        closeModal={() => {
          setCurrentStudent(null)
          setShowModal(false)
        }}
      />}
      {renderActiveWindow()}
      {renderPracticeModal()}
      {renderWarningModal()}
      <div className="trackingTableContainer">
        <table className={tableStyles}>
          <Header
            sorts={sorts}
            onClick={tableSort}
            checked={isHeaderChecked}
            onChangeCheckbox={() => {
              if (isHeaderChecked) setSelected([]);
              else selectAll();
            }}
          />
          <tbody className="tracking-table-body" onScroll={onScroll}>
            {reportData.map((data, i) => (
              <Row
                key={i}
                data={data}
                isSelected={selected.includes(data.studentId)}
                isLoading={loadingSelected.includes(data.studentId)}
                index={i}
                onRowSelected={onRowSelected}
                onUnassign={unassign}
                isPastWindow={isPastWindow}
                onDeepLink={(student) => {
                  const { studentId, first_name, last_name } = student;
                  // select student within roster
                  rosterSelect({
                    type: "student",
                    data: {
                      value: studentId,
                      label: `${first_name} ${last_name}`,
                    },
                  });
                  // navigate to skills diagnostic
                  props.history.push("/teacher/reports/type/skillsdiagnostic");
                }}
                openModal={(event) => {
                  event.stopPropagation();
                  setCurrentStudent(data);
                  setShowModal(true);
                }}
              />
            ))}
          </tbody>
        </table>
      </div>
      <div key="assignmentControls" className="assignmentControls">
      {shouldShowButton('benchmark') && (
        <Button
          onClick={() => attemptAssignment(ASSIGNMENT_TYPE.BENCHMARK, selected)}
          disabled={!isAssignableSelection || isPastWindow}
        >
          {t("Assign Benchmark Assessment")}
        </Button>
      )}
      {shouldShowButton('progressMonitoring') && (
        <Button
          onClick={() => attemptAssignment(ASSIGNMENT_TYPE.PROGRESS_MONITORING, selected)}
          disabled={!isAssignableSelection || isPastWindow}
        >
          {t("Assign Progress Monitor")}
        </Button>
      )}
        {shouldShowButton('progressMonitoring') && getUserData().calibrationEnabled && (
          <Button
          onClick={() => attemptAssignment(ASSIGNMENT_TYPE.CALIBRATION, selected)}
          disabled={!isAssignableSelection || isPastWindow}
        >
          {t("Assign Calibration")}
        </Button>
        )}
        {shouldShowButton('practice') && (
        <Button
          onClick={() => {
            const selection = cloneDeep(selected.map((s) => lookup[s]));
            setPracticeModalData(selection);
          }}
          disabled={!selected[0] || isPastWindow}
        >
          {t("Configure Practice")}
        </Button>
        )}
        {shouldShowButton('districtAssessment') && (
        <Button
          onClick={() => {
            const selection = cloneDeep(selected.map((s) => lookup[s]));
            if(selection) selection.assessmentMode = true;
            setPracticeModalData(selection);
          }}
          disabled={!selected[0] || isPastWindow}
        >
          {t("Configure District Assessment")}
        </Button>
        )}
        {shouldShowButton('unassign') && (
        <Button
          onClick={unassign}
          disabled={!isUnassignableSelection || isPastWindow}
        >
          {t("Unassign Assessment")}
        </Button>
        )}
      </div>
    </div>
  );

  function renderActiveWindow() {
    let activeWindow;
    if (reportData[0]) activeWindow = reportData[0].activeWindow

    if (!activeWindow) return

    const {dateTo, window} = activeWindow
    const [month, day] = moment(dateTo).format('M/DD').split("/");

    return (
      <div className="screener-info">
        <div className="screener-title-row">
          <div className="screener-title-text">
            {t("screening_window_set", {window})}
          </div>
          <div className="screener-type">{t("benchmark_screener")}</div>
          <div className="screener-date">
            <FaCheck /> {t("active_ends_on", {month, day})}
          </div>
        </div>
        <div>
          {t("active_screening_window")}
        </div>
      </div>
    );
  }

  function renderPracticeModal() {
    return <PracticeModal practiceModalData={practiceModalData} onClose={() => setPracticeModalData(null)} />
  }

  function renderWarningModal() {
    if (!warningModalData) return;
    return <WarningModal warningModalData={warningModalData} onClose={() => setWarningModalData(null)} />
  }

  // passed: how many checks have been passed already.
  // is relevant when this function is called recursively by a warning modal
  function attemptAssignment(type, studentIds, passed=0) {
    const students = cloneDeep(studentIds.map(id => lookup[id]));
    if (type === ASSIGNMENT_TYPE.CALIBRATION) {
        // Check if selection contains any students who are not assignable (i.e., grade not in [0, 1, 2, 3] or not isAssignable)
        const notAssignable = students.filter(s => ![0, 1, 2, 3, 4, 5, 6, 7, 8].includes(s.grade) || !s.isAssignable);
        if (notAssignable.length > 0 && passed < 1) {
            const assignable = students.filter(s => [0, 1, 2, 3, 4, 5, 6, 7, 8].includes(s.grade) && s.isAssignable).map(s => s.studentId);

            // Check if there are any assignable students left before proceeding
            if (assignable.length === 0) {
                return setWarningModalData({
                    message: t("no_assignable_students"),
                    cancelText: 'OK'
                });
            }

            return setWarningModalData({
                message: `You aren’t able to assign calibration to students who are have an assessment in progress. These students will therefore not receive this assignment:`,
                students: notAssignable,
                confirmText: 'Yes, Proceed with Remaining Students',
                cancelText: 'No, Cancel',
                onConfirm: () => attemptAssignment(type, assignable, 1)
            });
        }
    }else{
      // check if there is a future screening window
      const {hasFutureWindow} = students[0]
      if (hasFutureWindow && passed < 1) return setWarningModalData({
        message: t("replace_district_assessment"),
        confirmText: t("proceed_with_assignment"),
        cancelText: t("cancel_and_use_district_assignment"),
        onConfirm: () => attemptAssignment(type, studentIds, 1)
      })

      // check if selection contains any students who are not assignable
      const notAssignable = students.filter(s => !s.isAssignable)
      if (notAssignable[0] && passed < 2) {
        const assignable = students.filter(s => s.isAssignable).map(s => s.studentId)
        return setWarningModalData({
          message: `You aren’t able to re-assign students with an assessment in progress. These students will therefore not receive this assignment:`,
          students: notAssignable,
          confirmText: 'Yes, Proceed with Remaining Students',
          cancelText: 'No, Cancel',
          onConfirm: () => attemptAssignment(type, assignable, 2),
        })
      }

      // check if selection contains any under review students
      const scoringWarning = students.some(s => ['UNDER_REVIEW', 'SCORING'].includes(s.status))
      if (scoringWarning && passed < 3) return setWarningModalData({
        message: `This student's assessment has completed successfully and is being reviewed. It will score shortly. We recommend that you do not re-assign until 2 days after the assessment was taken.`,
        confirmText: 'Create a New Assignment Anyway',
        cancelText: 'Wait for Assignment To Score',
        onConfirm: () => attemptAssignment(type, studentIds, 3),
      })
    }


    // if all checks passed... proceed normally
    assign(type, studentIds)
  }

  function assign(type, studentIds) {
    const students = cloneDeep(studentIds.map((id) => lookup[id]));

    // transform manifest for all students
    for (const s of students) {
      if(type === ASSIGNMENT_TYPE.CALIBRATION){
        const manifest = getCalibrationManifest(s);
        s.manifest = manifest;
      }else{
        const locales = s.metadata.esl ? ['EN_US', 'ES_MX'] : ['EN_US']
        s.manifest = filterLocales(s.manifest, locales)
      }
    }

    setAssignmentState({ students, type });

    // go to assignment screen
    props.history.push("/teacher/reports/type/assignment");
  }

  async function unassign() {
    const payload = selected
      .map((id) => lookup[id])
      .map((student) => {
        const { entityId, entityType, assignmentId } = student;
        return { entityId, entityType, assignmentId, status: "UNASSIGNED" };
      });

    setLoadingSelected(selected);
    setSelected([]);
    const res = await updateBatchAssignments(payload);
    console.log("res", res);
    props.getData({ skipPending: true });
  }
}

const calibrationAssignments = [
  [
    {
      activityType: "dyslexia",
      locale: "EN_US",
      sequence: "CONTINUE"
    },
    {
      activityType: "spelling",
      locale: "EN_US",
      sequence: "CONTINUE"
    },
    {
      activityType: "assessment",
      locale: "EN_US",
      sequence: "CONTINUE"
    },
    {
      activityType: "readingComprehension",
      locale: "EN_US",
      sequence: "BREAK"
    }
  ],
  [
    {
      activityType: "dyslexia",
      locale: "EN_US",
      sequence: "CONTINUE"
    },
    {
      activityType: "nwf",
      locale: "EN_US",
      sequence: "CONTINUE"
    },
    {
      activityType: "spelling",
      locale: "EN_US",
      sequence: "CONTINUE"
    },
    {
      activityType: "assessment",
      locale: "EN_US",
      sequence: "CONTINUE"
    },
    {
      activityType: "readingComprehension",
      locale: "EN_US",
      sequence: "BREAK"
    }
  ],
  [
    {
      activityType: "dyslexia",
      locale: "EN_US",
      sequence: "CONTINUE"
    },
    {
      activityType: "nwf",
      locale: "EN_US",
      sequence: "CONTINUE"
    },
    {
      activityType: "spelling",
      locale: "EN_US",
      sequence: "CONTINUE"
    },
    {
      activityType: "assessment",
      locale: "EN_US",
      sequence: "CONTINUE"
    },
    {
      activityType: "readingComprehension",
      locale: "EN_US",
      sequence: "BREAK"
    }
  ],
  [
    {
      activityType: "nwf",
      locale: "EN_US",
      sequence: "CONTINUE"
    },
    {
      activityType: "spelling",
      locale: "EN_US",
      sequence: "CONTINUE"
    },
    {
      activityType: "assessment",
      locale: "EN_US",
      sequence: "CONTINUE"
    },
    {
      activityType: "readingComprehension",
      locale: "EN_US",
      sequence: "BREAK"
    }
  ],
  [
    {
      activityType: "spelling",
      locale: "EN_US",
      sequence: "CONTINUE"
    },
    {
      activityType: "assessment",
      locale: "EN_US",
      sequence: "CONTINUE"
    },
    {
      activityType: "readingComprehension",
      locale: "EN_US",
      sequence: "BREAK"
    }
  ],
  [
    {
      activityType: "spelling",
      locale: "EN_US",
      sequence: "CONTINUE"
    },
    {
      activityType: "assessment",
      locale: "EN_US",
      sequence: "CONTINUE"
    },
    {
      activityType: "readingComprehension",
      locale: "EN_US",
      sequence: "BREAK"
    }
  ],
  [
    {
      activityType: "spelling",
      locale: "EN_US",
      sequence: "CONTINUE"
    },
    {
      activityType: "assessment",
      locale: "EN_US",
      sequence: "CONTINUE"
    },
    {
      activityType: "readingComprehension",
      locale: "EN_US",
      sequence: "BREAK"
    }
  ],
  [
    {
      activityType: "spelling",
      locale: "EN_US",
      sequence: "CONTINUE"
    },
    {
      activityType: "assessment",
      locale: "EN_US",
      sequence: "CONTINUE"
    },
    {
      activityType: "readingComprehension",
      locale: "EN_US",
      sequence: "BREAK"
    }
  ],
  [
    {
      activityType: "spelling",
      locale: "EN_US",
      sequence: "CONTINUE"
    },
    {
      activityType: "assessment",
      locale: "EN_US",
      sequence: "CONTINUE"
    },
    {
      activityType: "readingComprehension",
      locale: "EN_US",
      sequence: "BREAK"
    }
  ]
]
function getCalibrationManifest(student){
  return calibrationAssignments[student.grade];
}



Tracking.propTypes = {
  onSort: PropTypes.func,
  onSelect: PropTypes.func,
  sorts: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      dir: PropTypes.string,
    })
  ),
  reportData: PropTypes.arrayOf(
    PropTypes.shape({
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      date: PropTypes.string,
      practice: PropTypes.number,
      total: PropTypes.number,
    })
  ),
};
